import React, { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ChevronDownIcon } from 'lucide-react';
import type { NavItem } from '../../data/navigation';
import { DesignMegaPanel, MarketingMegaPanel } from './MegaMenuPanels';
type NavDropdownProps = {
  label: string;
  mega: NonNullable<NavItem['mega']>;
};

// Full-width mega menu. The panel is positioned against the sticky header,
// so this wrapper is intentionally not `relative`.
export function NavDropdown({
  label,
  mega
}: NavDropdownProps) {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const closeTimer = useRef<number>();
  useEffect(() => {
    if (!open) return;
    const handleKey = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    const handleClick = (e: MouseEvent) => {
      if (!wrapperRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('keydown', handleKey);
    document.addEventListener('mousedown', handleClick);
    return () => {
      document.removeEventListener('keydown', handleKey);
      document.removeEventListener('mousedown', handleClick);
    };
  }, [open]);
  useEffect(() => () => window.clearTimeout(closeTimer.current), []);
  const show = () => {
    window.clearTimeout(closeTimer.current);
    setOpen(true);
  };
  const hide = () => {
    closeTimer.current = window.setTimeout(() => setOpen(false), 160);
  };
  const close = () => setOpen(false);
  return <div ref={wrapperRef} onMouseEnter={show} onMouseLeave={hide} data-id="element-670" data-mp="2g9z2fnrzribw">
      <button type="button" aria-expanded={open} aria-haspopup="true" onClick={() => setOpen(v => !v)} className={`flex items-center gap-1 rounded-sm text-sm font-medium transition-colors duration-150 ease-out hover:text-brand-red focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-red ${open ? 'text-brand-red' : 'text-gray-600'}`} data-id="element-671" data-mp="2g7mkham8h7yu">
        {label}
        <ChevronDownIcon className={`h-4 w-4 transition-transform duration-200 ease-out ${open ? 'rotate-180' : ''}`} aria-hidden="true" data-id="element-672" data-mp="xbayexer13uw" />
      </button>

      <AnimatePresence data-id="element-673" data-mp="oad2l2egs2ne">
        {open && <motion.div initial={{
        opacity: 0,
        y: -6
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -6
      }} transition={{
        duration: 0.18,
        ease: [0.23, 1, 0.32, 1]
      }} className="absolute inset-x-0 top-full z-50 border-t border-gray-100 bg-white shadow-xl" data-id="element-674" data-mp="o9bh03ztnxrf">
            <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8" data-id="element-675" data-mp="jdcwwslj11wt">
              {mega === 'marketing' ? <MarketingMegaPanel onNavigate={close} data-id="element-676" data-mp="4karadl4fjz0" /> : <DesignMegaPanel onNavigate={close} data-id="element-677" data-mp="9kxa9hzabp5l" />}
            </div>
            <div className="border-t border-gray-100 bg-gray-50" data-id="element-678" data-mp="9j48afzm9t3x">
              <div className="mx-auto flex max-w-7xl items-center justify-between gap-6 px-4 py-3 text-sm sm:px-6 lg:px-8" data-id="element-679" data-mp="3rrlgxc9zgkdd">
                <span className="text-gray-600" data-id="element-680" data-mp="mlez2nh436t">Not sure where to start? Talk to a real person in Las Vegas.</span>
                <a href="tel:17022134801" className="whitespace-nowrap font-bold text-brand-black transition-colors duration-150 ease-out hover:text-brand-red focus:outline-none focus-visible:underline" data-id="element-681" data-mp="3859xki5edsp7">
                  Call (702) 213-4801
                </a>
              </div>
            </div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}